import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  ClientCreateFormData,
  clientCreateSchema,
  ClientUpdateFormData,
  clientUpdateSchema,
  Client,
} from "../../schemas/clientSchema";
import ClientScreenSelection from "../subComponents/ClientScreenSelection";

interface ClientFormProps {
  initialData?: Partial<ClientUpdateFormData> | Partial<Client>;
  onSubmit: (data: ClientCreateFormData | ClientUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const [step, setStep] = useState(1);
  // Initialize selected screens from either screenIds or screens relationship
  const getInitialScreenIds = (): string[] => {
    const data = initialData as Partial<Client>;
    if (data?.screenIds) return data.screenIds;
    if (data?.screens)
      return data.screens.map((screen: { id: string }) => screen.id);
    return [];
  };
  const [selectedScreens, setSelectedScreens] = useState<string[]>(
    getInitialScreenIds()
  );
  const [clientDetails, setClientDetails] =
    useState<Partial<ClientCreateFormData> | null>(
      isUpdate && initialData
        ? {
            name: initialData.name,
            email: initialData.email,
            phoneNumber: initialData.phoneNumber,
            restaurantName: initialData.restaurantName,
          }
        : null
    );

  const form = useForm<ClientCreateFormData | ClientUpdateFormData>({
    resolver: zodResolver(isUpdate ? clientUpdateSchema : clientCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      email: initialData?.email || "",
      phoneNumber: initialData?.phoneNumber || "",
      restaurantName: initialData?.restaurantName || "",
      screenIds: (initialData as Partial<Client>)?.screenIds || [],
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = form;

  // Initialize step for update mode
  useEffect(() => {
    if (isUpdate && initialData) {
      setStep(1);
      // Extract screen IDs from either screenIds array or screens relationship
      const data = initialData as Partial<Client>;
      const screenIds =
        data.screenIds ||
        data.screens?.map((screen: { id: string }) => screen.id) ||
        [];
      setSelectedScreens(screenIds);
      setClientDetails({
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        restaurantName: data.restaurantName,
      });
    }
  }, [isUpdate, initialData]);

  // For create and update mode, use multi-step form
  const handleStep1Submit = async (
    data: ClientCreateFormData | ClientUpdateFormData
  ) => {
    setClientDetails(data);
    setStep(2);
  };

  const handleStep2Submit = () => {
    if (clientDetails) {
      onSubmit({
        ...clientDetails,
        screenIds: selectedScreens,
      } as ClientCreateFormData | ClientUpdateFormData);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <div className="space-y-4">
      {/* Step Indicator */}

      {step === 1 && (
        <form onSubmit={handleSubmit(handleStep1Submit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Name"
              placeholder="Enter client name"
              name="name"
              value={watch("name") || ""}
              onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("name")}
              error={errors.name}
            />
            <Input
              label="Email"
              placeholder="Enter email"
              name="email"
              value={watch("email") || ""}
              onChange={(e) => setValue("email", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("email")}
              error={errors.email}
            />
            <Input
              label="Phone Number"
              placeholder="Enter phone number"
              name="phoneNumber"
              value={watch("phoneNumber") || ""}
              onChange={(e) => setValue("phoneNumber", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("phoneNumber")}
              error={errors.phoneNumber}
              allowedChars="numeric"
            />
            <Input
              label="Restaurant Name"
              placeholder="Enter restaurant name"
              name="restaurantName"
              value={watch("restaurantName") || ""}
              onChange={(e) => setValue("restaurantName", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("restaurantName")}
              error={errors.restaurantName}
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded mb-4">
            <h3 className="font-semibold mb-2">Client Details</h3>
            <p className="text-sm text-gray-600">
              <strong>Name:</strong> {clientDetails?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Email:</strong> {clientDetails?.email}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Phone:</strong> {clientDetails?.phoneNumber}
            </p>
            {clientDetails?.restaurantName && (
              <p className="text-sm text-gray-600">
                <strong>Restaurant:</strong> {clientDetails.restaurantName}
              </p>
            )}
          </div>

          <div className="border rounded p-4 max-h-96 overflow-y-auto">
            <ClientScreenSelection
              selected={selectedScreens}
              setSelected={setSelectedScreens}
            />
          </div>

          <div className="flex justify-between space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              disabled={isLoading}
            >
              Previous
            </Button>
            <div className="flex space-x-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleStep2Submit}
                isLoading={isLoading}
                className="w-full sm:w-auto"
              >
                {isUpdate ? "Update Client" : "Create Client"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientForm;
